/**
 * @file LinkedPurchaseOrdersPanel.js
 * @version 1.3.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-19
 * @modified 2026-09-17
 * @description Shared panel rendered inside the contract / monthly-pricing
 *              modals. Lists the POs supplying this contract (auto-matched
 *              + manually linked) and, in edit mode, lets the user unlink
 *              auto-matches or add manual links via LinkPosDialog.
 *              For group contracts, pass entityIds (array) instead of
 *              entityId — the panel will fetch all members in parallel and
 *              merge the results (read-only; manual linking is disabled in
 *              multi-entity mode because the link target is ambiguous; moving,
 *              unlinking and removing an existing row still work, because each
 *              row remembers the member it came from).
 *
 *              A PO line counts against ONE contract or month only (the rule
 *              is in migrations/contract_po_line_allocation.sql). A PO this
 *              contract matched but that counts elsewhere is still listed, greyed,
 *              saying where it counts. Right-click a row to view the PO or, in
 *              edit mode, move it to another month or contract.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { formatDateShort } from '../utils/dateFormatters';
import LinkPosDialog from './LinkPosDialog';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import PurchaseOrderModal from './PurchaseOrderModal';
import { useSlideOutButton } from './SlideOutButton';
import './LinkedPurchaseOrdersPanel.css';

function LinkedPurchaseOrdersPanel({ kind, entityId, entityIds, mode = 'view', title = 'Linked Purchase Orders' }) {
  // kind: 'contract' for blends/compounds, 'monthly' for straights
  // entityId: customer_contracts.id or monthly_pricing.id (single-entity mode)
  // entityIds: array of ids for group contracts (multi-entity, view-only)
  // mode: 'view' | 'edit' — edit shows unlink + add-link controls; ignored
  //   when entityIds is provided (groups force view-only since per-row links
  //   would be ambiguous about which member they attach to).
  const ids = Array.isArray(entityIds) && entityIds.length > 0 ? entityIds : (entityId ? [entityId] : []);
  const isMultiEntity = ids.length > 1;
  // Link/unlink controls require BOTH single-entity AND edit mode. View mode
  // is read-only across the whole modal, so it would be inconsistent for the
  // PO panel to remain interactive. Multi-entity (group contract) view also
  // suppresses them because the link target would be ambiguous.
  const isEdit = !isMultiEntity && mode !== 'view';
  // Moving, unlinking and removing act on a row, and a row knows which member it
  // came from — so unlike adding a new link, they work on a group contract too.
  const canEditRows = mode !== 'view';
  const urlSegment = kind === 'contract' ? 'contracts' : 'monthly';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [linked, setLinked] = useState({ auto: [], manual: [], order: [] });
  // Where a PO here can be moved: the customer's other months / contracts.
  const [moveTargets, setMoveTargets] = useState([]);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [viewingPo, setViewingPo] = useState(null);
  const [busy, setBusy] = useState(false);

  const idsKey = ids.join(',');
  const load = useCallback(async () => {
    if (ids.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(ids.map(async (id) => {
        const r = await fetch(`${API_URL}/api/pricing/${urlSegment}/${id}/linked-pos`, { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }));
      // Merge — one row per PO line across the members, each remembering the
      // member it came from (_memberId). Where several members claim the same
      // line, the one it COUNTS against wins: for a group, a line counted by any
      // member counts here.
      const memberIds = new Set(ids.map(Number));
      const byLine = new Map();
      results.forEach((data, i) => {
        for (const source of ['order', 'manual', 'auto']) {
          for (const row of (data[source] || [])) {
            const tagged = { ...row, source, _memberId: ids[i] };
            const key = `${row.purchase_order_number}|${row.line_item_code || ''}`;
            const prev = byLine.get(key);
            if (!prev || (tagged.counted !== false && prev.counted === false)) byLine.set(key, tagged);
          }
        }
      });
      const merged = { auto: [], manual: [], order: [] };
      for (const row of byLine.values()) {
        const inGroup = row.counted === false
          && row.counted_against?.kind === kind
          && memberIds.has(Number(row.counted_against.id));
        const shown = inGroup
          ? { ...row, counted: true, _memberId: row.counted_against.id, counted_against: null }
          : row;
        merged[shown.source].push(shown);
      }
      setLinked(merged);
      // Every member of a group is the same customer and category, so one
      // member's destinations are the group's.
      const t = await fetch(`${API_URL}/api/pricing/${urlSegment}/${ids[0]}/move-targets`, { credentials: 'include' });
      setMoveTargets(t.ok ? await t.json() : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, urlSegment]);

  useEffect(() => { load(); }, [load]);

  const unlinkAuto = async (po) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/pricing/${urlSegment}/${po._memberId ?? entityId}/po-links`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: po.purchase_order_number, action: 'unlink' })
      });
      await load();
    } finally { setBusy(false); }
  };

  const removeManual = async (po) => {
    setBusy(true);
    try {
      await fetch(
        `${API_URL}/api/pricing/${urlSegment}/${po._memberId ?? entityId}/po-links/${encodeURIComponent(po.purchase_order_number)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      await load();
    } finally { setBusy(false); }
  };

  // Count a PO against `target` instead of where it counts now.
  const movePo = async (po, target) => {
    const from = po.counted
      ? { kind, id: po._memberId ?? entityId }
      : { kind: po.counted_against.kind, id: po.counted_against.id };
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/pricing/${urlSegment}/${target.id}/po-links/move`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: po.purchase_order_number, from_kind: from.kind, from_id: from.id })
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${r.status}`);
      }
      await load();
    } catch (e) {
      setError(`Couldn't move ${po.purchase_order_number}: ${e.message}`);
    } finally { setBusy(false); }
  };

  const addManualBatch = async (selectedPoNumbers) => {
    setBusy(true);
    try {
      // One sequential POST per PO — keeps the audit trail honest if any fail.
      for (const po of selectedPoNumbers) {
        await fetch(`${API_URL}/api/pricing/${urlSegment}/${entityId}/po-links`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ po_number: po, action: 'manual' })
        });
      }
      setShowSearchDialog(false);
      await load();
    } finally { setBusy(false); }
  };

  // Counted here first, then those counting elsewhere; delivery order within.
  const allRows = [
    ...linked.order.map(r => ({ ...r, source: 'order' })),
    ...linked.auto.map(r => ({ ...r, source: 'auto' })),
    ...linked.manual.map(r => ({ ...r, source: 'manual' }))
  ].sort((a, b) => (Number(b.counted !== false) - Number(a.counted !== false))
    || String(a.delivery_date || '').localeCompare(String(b.delivery_date || '')));
  const countedHere = allRows.filter(r => r.counted !== false).length;
  const countedElsewhere = allRows.length - countedHere;

  const ctx = useTableContextMenu({ rows: allRows, getRowId: r => `${r.source}-${r.purchase_order_number}` });

  // The move destinations for a row, nearest to its delivery first. Ten at most,
  // so a long straights contract doesn't run the menu off the screen.
  const rowActions = (po) => {
    const loadDay = String(po.delivery_date || po.date || '').slice(0, 10);
    const dayNo = (iso) => (iso ? Date.parse(`${iso}T00:00:00Z`) / 86400000 : null);
    const distance = (t) => {
      const d = dayNo(loadDay);
      const start = dayNo(t.window_start);
      const end = dayNo(t.window_end);
      if (d == null || start == null) return Number.MAX_SAFE_INTEGER;
      if (d < start) return start - d;
      if (end != null && d > end) return d - end;
      return 0;
    };
    const current = po.counted !== false
      ? { kind, id: po._memberId ?? entityId }
      : { kind: po.counted_against?.kind, id: po.counted_against?.id };
    const nearest = [...moveTargets]
      .sort((a, b) => distance(a) - distance(b) || String(a.window_start).localeCompare(String(b.window_start)))
      .slice(0, 10)
      .sort((a, b) => String(a.window_start).localeCompare(String(b.window_start)));
    const whyNot = !canEditRows
      ? 'Edit the contract to move POs'
      : po.source === 'order'
        ? 'Raised from a customer order — change the contract on the order line'
        : null;

    const moves = nearest.map(t => {
      const isCurrent = current.kind === kind && Number(current.id) === t.id;
      return {
        key: `move-${t.id}`,
        icon: isCurrent ? 'check_circle' : 'drive_file_move',
        label: isCurrent ? `Counts against ${t.label}` : `Move to ${t.label}`,
        active: isCurrent,
        disabled: isCurrent || !!whyNot || busy,
        title: isCurrent ? `${po.purchase_order_number} counts against ${t.label}` : (whyNot || `Count ${po.purchase_order_number} against ${t.label}`),
        onClick: () => movePo(po, t)
      };
    });
    // Counted against something that isn't one of this customer's months or
    // contracts for the product — say where, even though it can't be picked.
    if (po.counted === false && !nearest.some(t => current.kind === kind && Number(current.id) === t.id)) {
      moves.unshift({
        key: 'counted-elsewhere', icon: 'check_circle', active: true, disabled: true,
        label: `Counts against ${po.counted_against?.label || 'another contract'}`,
        title: `${po.purchase_order_number} counts against ${po.counted_against?.label || 'another contract'}`
      });
    }

    return [
      { key: 'view', readSafe: true, icon: 'visibility', label: 'View PO', onClick: () => setViewingPo(po.xero_purchase_order_id) },
      ...moves,
      po.source === 'manual'
        ? { key: 'remove', icon: 'delete', label: 'Remove link', disabled: !canEditRows || busy,
            title: canEditRows ? 'Remove the manual link' : 'Edit the contract to change its links', onClick: () => removeManual(po) }
        : { key: 'unlink', icon: 'link_off', label: 'Unlink', disabled: !canEditRows || busy || po.source === 'order',
            title: po.source === 'order' ? 'Raised from a customer order — change the contract on the order line'
              : canEditRows ? 'Stop this PO counting against this contract' : 'Edit the contract to change its links',
            onClick: () => unlinkAuto(po) }
    ];
  };

  // Re-measure slide-out buttons whenever the panel's content shape changes.
  // The parent modal calls useSlideOutButton on its own mount, but month rows
  // (and therefore this panel) only render after an async fetch — the parent's
  // measure runs before this panel exists in the DOM, so the "Link PO" button
  // and any future labelled slide-out controls inside the panel would be left
  // at their collapsed-only width without this hook.
  useSlideOutButton([mode, isEdit, canEditRows, loading, busy, allRows.length]);

  return (
    <div className="linked-pos-panel">
      <div className="linked-pos-panel-header">
        <h4>{title}</h4>
        <span className="linked-pos-summary">
          {loading
            ? 'Loading…'
            : `${countedHere} counted here${countedElsewhere ? ` · ${countedElsewhere} counted elsewhere` : ''} (${linked.auto.length} auto · ${linked.manual.length} manual${linked.order.length ? ` · ${linked.order.length} from orders` : ''}) — right-click a PO for more`}
        </span>
        {isEdit && (
          <button
            type="button"
            className="btn-slideout"
            onClick={() => setShowSearchDialog(true)}
            disabled={busy}
          >
            <div className="icon-wrapper"><span className="material-symbols-rounded">add_link</span></div>
            <span className="btn-label">Link PO</span>
          </button>
        )}
      </div>

      {error && <div className="linked-pos-error">Failed to load linked POs: {error}</div>}

      {!loading && allRows.length === 0 && !error && (
        <div className="linked-pos-empty">No POs are currently linked to this contract.</div>
      )}

      {allRows.length > 0 && (
        <div className="linked-pos-table-wrap">
          <table className="linked-pos-table">
            <thead>
              <tr>
                <th>PO #</th>
                <th>Product</th>
                <th>Description</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Unit</th>
                <th className="text-right">Total</th>
                <th>Order</th>
                <th>Delivery</th>
                <th>Source</th>
                {canEditRows && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {allRows.map(po => (
                // The modal looks a PO up by its Xero id. The PO number was
                // passed here, which matches no PO at all — so the link failed
                // on every contract, straights, blends and compounds alike.
                <tr
                  key={`${po.source}-${po.purchase_order_number}-${po.line_item_code || ''}`}
                  className={po.counted === false ? 'is-counted-elsewhere' : undefined}
                  title={po.counted === false ? `Counts against ${po.counted_against?.label} — right-click to move` : 'View PO — right-click for more'}
                  onClick={() => setViewingPo(po.xero_purchase_order_id)}
                  onContextMenu={ctx.onRowContextMenu(po)}
                >
                  <td><strong>{po.purchase_order_number}</strong></td>
                  <td><span className="po-code-badge">{po.line_item_code || '—'}</span></td>
                  <td className="po-desc">{po.line_description || '—'}</td>
                  <td className="text-right">{formatQuantity(po.line_quantity) || '—'}</td>
                  <td className="text-right">{po.line_unit_amount != null ? formatPrice(po.line_unit_amount) : '—'}</td>
                  <td className="text-right">{po.line_amount != null ? formatCurrency(po.line_amount) : '—'}</td>
                  <td className="nowrap">{formatDateShort(po.date)}</td>
                  <td className="nowrap">{formatDateShort(po.delivery_date)}</td>
                  <td>
                    <span className={`source-badge source-${po.source}`}>{po.source.toUpperCase()}</span>
                    {po.counted === false && (
                      <span className="counted-elsewhere-note">Counts against {po.counted_against?.label}</span>
                    )}
                  </td>
                  {canEditRows && (
                    // Inside the clickable row: without this, unlinking a PO
                    // also tried to open it.
                    <td onClick={(e) => e.stopPropagation()}>
                      {po.source !== 'manual' ? (
                        <button
                          className={`btn-icon${po.source === 'order' ? ' btn-disabled' : ''}`}
                          title={po.source === 'order'
                            ? 'Raised from a customer order — change the contract on the order line'
                            : 'Unlink (excludes from auto-match)'}
                          onClick={() => unlinkAuto(po)}
                          disabled={busy || po.source === 'order'}
                        >
                          <span className="material-symbols-rounded">link_off</span>
                        </button>
                      ) : (
                        <button className="btn-icon btn-danger" title="Remove manual link" onClick={() => removeManual(po)} disabled={busy}>
                          <span className="material-symbols-rounded">delete</span>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showSearchDialog && (
        <LinkPosDialog
          kind={kind}
          entityId={entityId}
          alreadyLinked={new Set(allRows.map(r => r.purchase_order_number))}
          onClose={() => setShowSearchDialog(false)}
          onLink={addManualBatch}
          onViewPo={(xeroPoId) => setViewingPo(xeroPoId)}
        />
      )}

      <TableContextMenu ctx={ctx} rowActions={rowActions} />

      {viewingPo && (
        <PurchaseOrderModal
          purchaseOrderId={viewingPo}
          onClose={() => setViewingPo(null)}
        />
      )}
    </div>
  );
}

export default LinkedPurchaseOrdersPanel;
